import React, { useEffect, useMemo, useState } from 'react'
import { union } from 'lodash'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandText } from '@/utils/brand-typography'
import { TabInput } from '@/modules/base/components/inputs'
import { dateConstants } from '@/modules/base/util/constants'
import { useAppSelector } from '@/redux/store'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import LegacyPlanHint from '@/modules/base/components/modal/upgrade-modal/subscription-selection/legacy-plan-hint/legacy-plan-hint'
import { isLegacyPlan } from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/isLegacyPlan'
import { ISubscriptionSelectionItem } from '../../types'
import { getMaxYearlySaving } from '../../_helpers/getPlanPrice'
import PlanCard, { PLAN_STATUS, PlanStatus } from './plan-card'

const PERIODS = [dateConstants.MONTH, dateConstants.YEAR]

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  periodSwitch: {
    padding: 4,
    border: `1px solid ${theme.palette.brand.divider}`,
    borderRadius: theme.borderRadiusPill,
    background: theme.palette.background.paper,
    '& .MuiTabs-root': {
      minHeight: 0,
    },
    boxShadow: theme.shadowCard,

  },
  periodTab: {
    minHeight: 0,
    padding: '8px 16px',
    border: 'none !important',
    borderRadius: `${theme.borderRadiusPill} !important`,
    background: 'none',
    color: theme.palette.brand.text.heading,
    fontSize: theme.brandTypography.text.regular.fontSize,
    '&:hover': {
      color: theme.palette.brand.text.primary,
    },
  },
  periodTabSelected: {
    background: 'linear-gradient(94.4deg, #FBECCC 0%, #D0DFF3 100%)',
    color: `${theme.palette.brand.text.primary} !important`,
  },
  periodIndicator: {
    display: 'none',
  },
  // fixed card width (design: 316 px), the row of cards is centred below the switch. The cards share
  // the rows of this grid (subgrid, see PlanCard), so heading, description, price, promotion, button and
  // features start at the same height in every card - the spacing comes from the cards, not from row gaps.
  plans: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, 316px)',
    gridAutoRows: 'auto',
    justifyContent: 'center',
    alignItems: 'stretch',
    columnGap: 16,
    rowGap: 0,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      rowGap: 16,
    },
  },
  hint: {
    width: '100%',
  },
  // the "*" behind the prices of the cards, centred below them
  priceHint: {
    maxWidth: 640,
    margin: 0,
    color: theme.palette.brand.text.secondary,
    textAlign: 'center',
    ...brandText(theme, 'tiny'),
  },
  empty: {
    textAlign: 'center',
  },
}))

interface PlanStepProps {
  subscriptionType: string
  plans: ISubscriptionSelectionItem[]
  items: ShoppingCartItems
  period?: string
  onSelect: (plan: ISubscriptionSelectionItem, period: string) => void
  // an existing customer keeps the current plan and goes on to the add-ons - with the period chosen here
  onContinueWithCurrent?: (period: string) => void
}

const PlanStep = ({ subscriptionType, plans, items, period: periodProps, onSelect, onContinueWithCurrent }: PlanStepProps) => {
  const classes = useStyles()
  const texts = lang.upgrade.wizard.plans
  const [period, setPeriod] = useState<string>(periodProps || dateConstants.YEAR)
  const activeSubscriptions = useAppSelector(state => state.subscription.details.subscriptions)
  const activeSubscription = activeSubscriptions.find(sub => sub.typeOfSubscription === subscriptionType)

  const availablePeriods = useMemo(() => {
    const periods = union(plans.flatMap(plan => plan.prices.map(price => price.period)))
    return PERIODS.filter(p => periods.includes(p))
  }, [plans])

  // the yearly payment is preselected, unless a plan only exists with another interval
  useEffect(() => {
    if (availablePeriods.length && !availablePeriods.includes(period)) setPeriod(availablePeriods[0])
  }, [availablePeriods])

  const maxYearlySaving = useMemo(() => getMaxYearlySaving(plans), [plans])
  const selectedPlanName = Object.values(items?.[subscriptionType] ?? {}).find(item => item.type)?.name

  // existing customers: the plans are ordered from small to large, so the position of the current
  // plan tells which ones are an upgrade and which ones cannot be booked any more
  const currentIndex = activeSubscription && !activeSubscription.isTrial
    ? plans.findIndex(plan => plan.name === activeSubscription.type)
    : -1
  const hasCurrentPlan = currentIndex >= 0
  const getStatus = (plan: ISubscriptionSelectionItem, index: number): PlanStatus => {
    if (index === currentIndex) return PLAN_STATUS.current
    if (plan.disabled || (hasCurrentPlan && index < currentIndex)) return PLAN_STATUS.locked
    return PLAN_STATUS.available
  }
  // the recommendation only makes sense for a plan the customer can still book as an upgrade
  const showRecommended = (plan: ISubscriptionSelectionItem, index: number) => Boolean(plan.bestseller) && (!hasCurrentPlan || index > currentIndex)

  if (!plans.length) {
    return <div className={classes.empty} dangerouslySetInnerHTML={{ __html: lang.upgrade.wizard.selection.noUpgradeAvailable }} />
  }

  const periodLabels = {
    [dateConstants.MONTH]: texts.monthly,
    [dateConstants.YEAR]: maxYearlySaving > 0 ? texts.yearlySaving(maxYearlySaving) : texts.yearly,
  }

  return (
    <section className={classes.root} data-qs-id='checkout-plan-step'>
      {availablePeriods.length > 1 && (
        <TabInput
          customClass={classes.periodSwitch}
          variant='standard'
          dataQsId='checkout-period-switch'
          value={availablePeriods.indexOf(period)}
          tabs={availablePeriods.map(p => ({ key: p, label: periodLabels[p] }))}
          muiClasses={{ tab: classes.periodTab, selected: classes.periodTabSelected, indicator: classes.periodIndicator }}
          onChange={(index: number) => setPeriod(availablePeriods[index])}
        />
      )}
      {isLegacyPlan(activeSubscription, plans) && <div className={classes.hint}><LegacyPlanHint /></div>}
      <div className={classes.plans}>
        {plans.map((plan, index) => (
          <PlanCard
            key={plan.name}
            plan={plan}
            period={period}
            status={getStatus(plan, index)}
            isUpgrade={hasCurrentPlan}
            isCurrentPrimary={currentIndex === plans.length - 1}
            isSelected={selectedPlanName === plan.name}
            showRecommended={showRecommended(plan, index)}
            onSelect={(selectedPlan) => onSelect(selectedPlan, period)}
            onContinue={onContinueWithCurrent && (() => onContinueWithCurrent(period))}
          />
        ))}
      </div>
      <p className={classes.priceHint} data-qs-id='checkout-plan-price-hint'>
        {period === dateConstants.YEAR ? lang.subscriptionSelectionHintYear : lang.subscriptionSelectionHintMonth}
      </p>
    </section>
  )
}

export default PlanStep
